import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Alert,
  Spinner,
} from "reactstrap";
import styles from "../../styles/public/time-selector.module.css";
import api from "../../services/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const NoTimesAlert = () => (
  <Alert color="warning" className="text-center" fade={false}>
    No times available
  </Alert>
);

// Helper: format a Date as ISO string in a specific IANA timezone (e.g., America/Chicago)
function toTimeZoneISOString(date, timeZone = "America/Chicago") {
  // Format using DateTimeFormat to extract components in that timezone
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const parts = Object.fromEntries(formatter.formatToParts(date).map(p => [p.type, p.value]));
  const isoLocal = `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
  
  // Offset string (like -05:00)
  const offset = -date.getTimezoneOffset(); // in minutes
  const sign = offset >= 0 ? "+" : "-";
  const absOffset = Math.abs(offset);
  const hh = String(Math.floor(absOffset / 60)).padStart(2, "0");
  const mm = String(absOffset % 60).padStart(2, "0");
  
  return `${isoLocal}${sign}${hh}:${mm}`;
}

// Helper: add hours/minutes/seconds to a Date
function addDuration(date, durationStr) {
  const [h, m, s] = durationStr.split(":").map(Number);
  return new Date(date.getTime() + ((h * 3600 + m * 60 + (s || 0)) * 1000));
}

function getTimeOfDay(hour) {
  if (hour < 12) return "Morning";
  if (hour >= 17) return "Evening";
  return "Afternoon";
}

// Helper: format 24-hour time (HH:MM:SS or HH:MM) to 12-hour format (H:MM AM/PM)
function formatTo12Hour(time24) {
  if (!time24) return "N/A";
  const [hour, minute] = time24.split(":").map(Number);
  const hour12 = hour % 12 || 12;
  const period = hour < 12 ? "AM" : "PM";
  return `${hour12}:${String(minute).padStart(2, "0")} ${period}`;
}

const TimeSelector = ({
  selectedDate,
  selectedTime,
  onTimeSelect,
  prices,
  displayPrices = true,
  selectedService,
  organization,
}) => {
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [business_hours, setBusinessHours] = useState([]);

  useEffect(() => {
    if (selectedDate && selectedService?.id) {
      setLoading(true);

      const orgTz = organization?.timezone || "America/Chicago";
      const isoDate = toTimeZoneISOString(new Date(selectedDate), orgTz);
      const now = new Date();
      const durationStr = organization?.online_buffer_time || "01:30:00";
      const timeBuffer = addDuration(now, durationStr);

      api
        .get(
          `/available-times/?date=${isoDate}&service=${selectedService.id}`,
          {
            skipAuthRedirect: true,
          }
        )
        .then((response) => {
          const times = response.data
            .filter((slot) => new Date(slot["slot"]) > timeBuffer)
            .map((slot) => {
              const d = new Date(slot["slot"]);
              const h = d.getHours();
              const m = d.getMinutes();
              return {
                time24: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`,
                timeOfDay: getTimeOfDay(h),
              };
            });

          setAvailableSlots(times);
        })
        .catch((error) => {
          console.error("Fetch availability error:", error);
          setAvailableSlots([]);
        })
        .finally(() => setLoading(false));
    }
  }, [
    selectedDate,
    selectedService?.id,
    organization?.online_buffer_time,
    organization?.timezone,
  ]);

  useEffect(() => {
    if (!selectedDate) return;
    
    api
      .get(`/business-hours/?date=${selectedDate.toISOString().split("T")[0]}`,
        {
          skipAuthRedirect: true,
        }
      )
      .then((response) => {
        setBusinessHours(response.data);
      })
      .catch((error) => {
        console.error("Fetch business hours error:", error);
        setBusinessHours([]);
      });
  }, [selectedDate]);

  const generateTimeSlots = (startTime, endTime, price) => {
    const [startHour, startMinute] = startTime.split(":").map(Number);
    const [endHour, endMinute] = endTime.split(":").map(Number);

    // Filter availableSlots that fall within this pricing tier's time range
    return availableSlots
      .filter((slot) => {
        const [hour, minute] = slot.time24.split(":").map(Number);
        const slotMinutes = hour * 60 + minute;
        const startMinutes = startHour * 60 + startMinute;
        const endMinutes = endHour * 60 + endMinute;
        
        return slotMinutes >= startMinutes && slotMinutes < endMinutes;
      })
      .map((slot) => {
        const [hour, minute] = slot.time24.split(":").map(Number);
        const hour12 = hour % 12 || 12;
        const period = hour < 12 ? "AM" : "PM";
        const timeStr12 = `${hour12}:${String(minute).padStart(2, "0")} ${period}`;
        
        return {
          time: timeStr12,
          price,
          hour24: hour,
          minute: minute,
          timeOfDay: slot.timeOfDay,
        };
      });
  };

  const getTimeSlotsAndPrice = (date) => {
    if (!date || !prices?.length)
      return { timeSlots: {}, price: null };

    const dateStr = new Date(date).toISOString().split("T")[0];
    const dayData = prices.find((p) => p.date === dateStr) || {};
    const tiers = dayData.tiers || [];

    const timeSlots = {};
    const price = {};

    tiers.forEach((tier) => {
      const period = tier.period;
      price[period] = `$${tier.numeric_price} /total`;

      timeSlots[period] = tier.details.flatMap((detail) =>
        generateTimeSlots(detail.start_time, detail.end_time, tier.price)
      );
    });

    return { price, timeSlots };
  };

  const { price, timeSlots } = selectedDate
    ? getTimeSlotsAndPrice(selectedDate)
    : { price: null, timeSlots: {} };

  const handleTimeClick = (time, price) => {
    onTimeSelect(time, price);
  };

  const renderTimeSection = (periods, priceValue, className, service) => (
    <Card key={priceValue} className={`mb-4 ${className}`} style={{ borderRadius: "20px" }}>
      <CardBody className={styles["time-selector-card-body"]}>
        <Row>
          <Col xs="12" md="6" className={`mb-4 mb-md-0 text-start ${styles["col-mobile-full"]}`}>
            {priceValue && displayPrices && (
              <div className="mb-3">
                <span className={styles["price-amount"]}>
                  {priceValue.split("/")[0]}
                </span>
                <span className={styles["price-total"]}>/total</span>
              </div>
            )}
            <div>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {service?.description}
              </ReactMarkdown>
            </div>
          </Col>
          <Col xs="12" md="6" className={styles["col-mobile-full"]}>
            {["Morning", "Afternoon", "Evening"].map((timeOfDay) => {
              const filteredSlots = periods.flatMap(
                (period) =>
                  timeSlots[period]?.filter(
                    (slot) => slot.timeOfDay === timeOfDay
                  ) || []
              );
              return filteredSlots.length > 0 ? (
                <div key={timeOfDay} className="mb-4">
                  <h5 className="mb-3 text-md-start text-center">{timeOfDay}</h5>
                  <div
                    className={`d-flex flex-wrap gap-2 justify-content-md-start justify-content-center ${styles["time-slots-mobile"]}`}
                  >
                    {filteredSlots.map((slot) => (
                      <Button
                        key={slot.time}
                        className={`${styles["time-slot"]} ${
                          selectedTime === slot.time ? styles.selected : ""
                        }`}
                        onClick={() =>
                          handleTimeClick(
                            slot.time,
                            parseFloat(priceValue.replace(/[^0-9.]/g, ""))
                          )
                        }
                        disabled={slot.price === null || slot.time === "N/A"}
                      >
                        {slot.time}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : null;
            })}
          </Col>
        </Row>
      </CardBody>
    </Card>
  );

  const periods = Object.keys(price || {});
  const allPrices = periods.map((p) => price[p]);
  const isSamePrice =
    allPrices.length > 0 && allPrices.every((p) => p === allPrices[0]);

  const renderAvailableTimes = () => {
    if (loading) return <div className="text-center py-4"><Spinner color="primary" /></div>;
    if (availableSlots.length === 0) return <NoTimesAlert />;

    if (isSamePrice) {
      return renderTimeSection(periods, price[periods[0]], "all-day", selectedService);
    }

    const validPeriods = periods.filter(
      (period) => (timeSlots[period]?.length ?? 0) > 0
    );

    if (validPeriods.length === 0) return <NoTimesAlert />;

    return validPeriods.map((period) =>
      renderTimeSection(
        [period],
        price[period],
        period.toLowerCase().replace(/\s+/g, "-"),
        selectedService
      )
    );
  };

  const displayDate = new Intl.DateTimeFormat("en-US", {
    timeZone: organization?.timezone || "America/Chicago",
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(new Date(selectedDate));

  return (
    <Container className={`${styles["time-selector"]} my-5 mx-auto`}>
      <h4 className={styles["time-selector-header"]}>
        Please select a time for {displayDate}
      </h4>
      <div className={styles["time-selector-subheader"]}>
          (Open from {formatTo12Hour(business_hours.start_time)} to {formatTo12Hour(business_hours.end_time)})
      </div>
      {renderAvailableTimes()}
    </Container>
  );
};

export default TimeSelector;
